import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useMemo,
} from 'react';
import { Disclosure, Popover, Tab, Portal } from '@headlessui/react';
import {
  ArrowDownIcon,
  ArrowRightIcon,
  BookOpenIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  LightningBoltIcon,
  ReplyIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  XIcon,
} from '@heroicons/react/outline';
import Modal from 'components/Modal/HeadlessUiModal';
import classNames from 'styles/utils/classNames';
import { formatGEXValue } from 'utils/shared/format';
import { isBetweenHelpSurfaces } from 'utils/client/helpPointerTransit';
import {
  GEXActionIcon,
  GEXChapter,
  GEXFact,
  GEXLesson,
  GEXLessonId,
  GEXTone,
  GEX_DEEPER_READING,
  GEX_SPEED,
  gexChapters,
  gexGuideControls,
  gexLessons,
} from 'components/GEXEducation/lessons';
import {
  LevelRail,
  OptionCell,
  OptionCells,
  RailLevel,
  RegimeVisual,
  WindowBars,
} from 'components/GEXEducation/GEXLiveVisuals';

/** The live board the guide draws on, so its chapters teach with the trader's own numbers. */
export interface GEXLiveBoard {
  ticker: string;
  spot: number;
  netGEX: number;
  callWall: number | null;
  putWall: number | null;
  zeroGamma: number | null;
  zeroDte: number;
  weekly: number;
  monthly: number;
  live: boolean;
  /** The IV shift the board is simulated at, if any. */
  ivShift: number | null;
}

interface EducationContext {
  enabled: boolean;
  activate: (
    id: string | null,
    dismiss?: () => void,
    pinned?: boolean,
    ownsFocus?: () => boolean,
    intent?: Wake,
  ) => boolean;
  release: (id: string) => void;
  openGuide: (chapter?: GEXChapter, returnTo?: HTMLElement | null) => void;
  guideTrigger: React.RefObject<HTMLButtonElement>;
}

const Context = createContext<EducationContext>({
  enabled: false,
  activate: () => false,
  release: () => {},
  openGuide: () => {},
  guideTrigger: { current: null },
});
export const useGEXEducation = () => useContext(Context);

/** The key levels as rail markers, for the level cards and the guide. */
export const boardLevels = ({
  callWall,
  putWall,
  zeroGamma,
}: Pick<GEXLiveBoard, 'callWall' | 'putWall' | 'zeroGamma'>): RailLevel[] => {
  const levels: (RailLevel | null)[] = [
    callWall ? { key: 'callWall', label: 'Call Wall', price: callWall, tone: 'call' } : null,
    zeroGamma ? { key: 'zero', label: 'Zero Gamma', price: zeroGamma, tone: 'zero' } : null,
    putWall ? { key: 'putWall', label: 'Put Wall', price: putWall, tone: 'put' } : null,
  ];
  return levels.filter((level): level is RailLevel => level !== null);
};

function chapterVisual(chapter: GEXChapter, board: GEXLiveBoard | null) {
  if (!board) return null;
  if (chapter === 'regime') return <RegimeVisual netGEX={board.netGEX} />;
  if (chapter === 'levels') return <LevelRail spot={board.spot} levels={boardLevels(board)} />;
  if (chapter === 'expirations')
    return <WindowBars zeroDte={board.zeroDte} weekly={board.weekly} monthly={board.monthly} />;
  return null;
}

interface ProviderProps {
  enabled: boolean;
  board?: GEXLiveBoard | null;
  children: React.ReactNode;
}

export default function GEXEducationProvider({ enabled, board = null, children }: ProviderProps) {
  // A ref closes the previous card without broadcasting hover changes to every heatmap cell.
  const active = useRef<{
    id: string;
    dismiss: () => void;
    pinned: boolean;
    ownsFocus?: () => boolean;
  } | null>(null);
  const guideVisible = useRef(false);
  const activate = useCallback(
    (
      id: string | null,
      dismiss?: () => void,
      pinned = false,
      ownsFocus?: () => boolean,
      intent: Wake = 'hover',
    ) => {
      if (
        id &&
        (guideVisible.current ||
          (active.current &&
            active.current.id !== id &&
            !pinned &&
            intent === 'hover' &&
            (active.current.pinned || active.current.ownsFocus?.())))
      )
        return false;
      const previous = active.current;
      if (previous?.id !== id) {
        active.current = null;
        previous?.dismiss();
      }
      active.current = id && dismiss ? { id, dismiss, pinned, ownsFocus } : null;
      return true;
    },
    [],
  );
  const release = useCallback((id: string) => {
    if (active.current?.id === id) active.current = null;
  }, []);
  const [guideChapter, setGuideChapter] = useState<GEXChapter | null>(null);
  const returnRef = useRef<HTMLElement | null>(null);
  const guideTrigger = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const openGuide = useCallback(
    (chapter: GEXChapter = 'regime', returnTo?: HTMLElement | null) => {
      returnRef.current =
        returnTo ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      activate(null);
      guideVisible.current = true;
      window.dispatchEvent(new Event('gex-open-guide'));
      setGuideChapter(chapter);
    },
    [activate],
  );
  const context = useMemo(
    () => ({ enabled, activate, release, openGuide, guideTrigger }),
    [enabled, activate, release, openGuide],
  );
  const closeGuide = () => {
    setGuideChapter(null);
    timers.current.push(
      setTimeout(() => {
        const target = returnRef.current?.isConnected ? returnRef.current : guideTrigger.current;
        target?.focus({ preventScroll: true });
        activate(null);
        guideVisible.current = false;
      }, 250),
    );
  };
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const showSection = (section: string) => {
    closeGuide();
    timers.current.push(
      setTimeout(() => {
        const element = document.querySelector<HTMLElement>(`[data-gex-section="${section}"]`);
        if (!element) return;
        element.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'center',
        });
        element.setAttribute('tabindex', '-1');
        element.focus({ preventScroll: true });
        // The dialog's leave transition returns focus to the guide button when it ends.
        timers.current.push(
          setTimeout(() => {
            if (document.activeElement === guideTrigger.current)
              element.focus({ preventScroll: true });
          }, 400),
        );
        element.classList.add('ring-2', 'ring-brand-primary');
        timers.current.push(
          setTimeout(() => element.classList.remove('ring-2', 'ring-brand-primary'), 1800),
        );
      }, 250),
    );
  };
  return (
    <Context.Provider value={context}>
      {children}
      {enabled && (
        <Modal
          isOpen={guideChapter !== null}
          handleClose={closeGuide}
          ariaLabel="How to use Gamma Exposure"
          initialFocus={closeRef}
          classNameMaxWidth="sm:max-w-[920px]"
          className="bg-palette-black-1"
          dialogClassName="z-[90]"
        >
          <div className="flex h-[min(820px,90dvh)] flex-col">
            <div className="relative shrink-0 overflow-hidden border-b border-white/[0.06] px-5 pb-5 pt-5 sm:px-6">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-accent-blue/[0.09] blur-3xl"
              />
              <div className="relative flex items-start justify-between gap-4">
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-blue">
                    Gamma Exposure Guide
                  </span>
                  <h2 className="mt-1 text-xl font-bold tracking-tight text-text-primary sm:text-2xl">
                    Trade with the gamma map
                  </h2>
                  <p className="mt-1 text-sm text-text-secondary">
                    Five steps from the regime to the trade.
                  </p>
                </div>
                <button
                  ref={closeRef}
                  type="button"
                  onClick={closeGuide}
                  aria-label="Close GEX guide"
                  className="rounded-lg p-2.5 text-text-secondary hover:bg-white/[0.05] hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="relative mt-4 flex items-center gap-3 rounded-xl bg-accent-blue/[0.08] px-4 py-3 ring-1 ring-inset ring-accent-blue/25">
                <LightningBoltIcon
                  className="h-5 w-5 shrink-0 text-accent-blue"
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-bold text-text-primary">{GEX_SPEED.title}</h3>
                  <p className="text-[13px] leading-5 text-text-secondary">{GEX_SPEED.body}</p>
                </div>
                {board?.live && (
                  <span className="flex shrink-0 items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-bullish-green">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-bullish-green opacity-60 motion-reduce:animate-none" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-bullish-green" />
                    </span>
                    Live
                  </span>
                )}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-5 pt-4 sm:px-6">
              <Tab.Group
                selectedIndex={Math.max(
                  0,
                  gexChapters.findIndex((chapter) => chapter.id === guideChapter),
                )}
                onChange={(index) => setGuideChapter(gexChapters[index].id)}
              >
                <Tab.List className="flex gap-2 overflow-x-auto pb-1 sm:grid sm:grid-cols-5">
                  {gexChapters.map((chapter, index) => (
                    <Tab
                      key={chapter.id}
                      className={({ selected }) =>
                        classNames(
                          'min-w-[148px] shrink-0 rounded-xl px-3 py-2.5 text-left ring-1 ring-inset transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary sm:min-w-0',
                          selected
                            ? 'bg-accent-blue/[0.12] ring-accent-blue/50'
                            : 'bg-white/[0.02] ring-white/[0.07] hover:bg-white/[0.05]',
                        )
                      }
                    >
                      <span className="block font-mono text-[10px] font-bold text-accent-blue">
                        0{index + 1}
                      </span>
                      <span className="mt-0.5 block text-sm font-semibold leading-5 text-text-primary">
                        {chapter.title}
                      </span>
                    </Tab>
                  ))}
                </Tab.List>
                <Tab.Panels className="mt-4 grid">
                  {gexChapters.map((chapter) => {
                    const visual = chapterVisual(chapter.id, board);
                    return (
                      <Tab.Panel
                        key={chapter.id}
                        unmount={false}
                        // Inactive panels reserve the tallest chapter's space in the same grid
                        // cell. Visibility keeps them out of focus and the accessibility tree.
                        className="col-start-1 row-start-1 rounded-xl bg-white/[0.02] p-4 ring-1 ring-inset ring-white/[0.07] focus:outline-none sm:p-5 [&[hidden]]:pointer-events-none [&[hidden]]:invisible [&[hidden]]:!block"
                      >
                        <h3 className="text-pretty text-lg font-bold leading-7 tracking-tight text-text-primary">
                          {chapter.claim}
                        </h3>
                        <div
                          className={classNames(
                            'mt-4 grid gap-5',
                            visual && 'sm:grid-cols-[minmax(0,1fr)_280px]',
                          )}
                        >
                          <div>
                            <ol className="space-y-2.5">
                              {chapter.steps.map((step, index) => (
                                <li key={step} className="flex gap-3">
                                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-blue/15 text-[11px] font-bold text-accent-blue">
                                    {index + 1}
                                  </span>
                                  <span className="text-pretty pt-0.5 text-sm leading-5 text-text-primary">
                                    {step}
                                  </span>
                                </li>
                              ))}
                            </ol>
                            <button
                              type="button"
                              onClick={() => showSection(chapter.section)}
                              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                            >
                              {chapter.action}
                              <ArrowRightIcon className="h-4 w-4" />
                            </button>
                          </div>
                          {visual && board && (
                            <div className="self-start rounded-xl bg-palette-black-1 p-3 ring-1 ring-inset ring-white/[0.07]">
                              <span className="mb-2.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-text-faded">
                                <span>
                                  {board.ticker}{' '}
                                  {board.ivShift
                                    ? `at IV ${board.ivShift > 0 ? '+' : ''}${(
                                        board.ivShift * 100
                                      ).toFixed(0)}%`
                                    : 'right now'}
                                </span>
                                {chapter.id === 'regime' && (
                                  <span className="tabular-nums text-text-primary">
                                    Net GEX {formatGEXValue(board.netGEX)}
                                  </span>
                                )}
                              </span>
                              {visual}
                            </div>
                          )}
                        </div>
                        <dl className="mt-5 grid gap-x-6 gap-y-4 border-t border-white/[0.06] pt-4 sm:grid-cols-2 lg:grid-cols-3">
                          {chapter.fields.map((id) => (
                            <div key={id}>
                              <dt className="text-[10px] font-bold uppercase tracking-wider text-accent-blue">
                                {gexLessons[id].title}
                              </dt>
                              <dd className="mt-1 text-[13px] font-semibold leading-5 text-text-primary">
                                {gexLessons[id].claim}
                              </dd>
                              {(gexLessons[id] as GEXLesson).facts && (
                                <dd className="mt-1">
                                  <FactList facts={(gexLessons[id] as GEXLesson).facts!} small />
                                </dd>
                              )}
                            </div>
                          ))}
                        </dl>
                      </Tab.Panel>
                    );
                  })}
                </Tab.Panels>
              </Tab.Group>
              <GuideControls />
              <div className="mt-5">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-faded">
                  Go deeper
                </h3>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {GEX_DEEPER_READING.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between gap-3 rounded-lg bg-white/[0.02] p-3 text-sm font-medium text-text-primary ring-1 ring-inset ring-white/[0.07] hover:ring-accent-blue/50"
                    >
                      <span className="flex items-center gap-2.5">
                        <BookOpenIcon
                          className="h-4 w-4 shrink-0 text-accent-blue"
                          aria-hidden="true"
                        />
                        {link.title}
                      </span>
                      <ArrowRightIcon className="h-4 w-4 shrink-0 text-accent-blue transition-transform group-hover:translate-x-0.5" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </Context.Provider>
  );
}

/** A compact reference inside the guide, including every lesson hidden beside touch controls. */
function GuideControls() {
  return (
    <Disclosure as="section" className="mt-5 rounded-xl ring-1 ring-inset ring-white/[0.09]">
      {({ open }) => (
        <>
          <Disclosure.Button className="flex w-full items-center justify-between gap-3 rounded-xl bg-white/[0.02] px-4 py-3 text-left hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary">
            <span className="flex items-center gap-3">
              <InformationCircleIcon
                className="h-5 w-5 shrink-0 text-accent-blue"
                aria-hidden="true"
              />
              <span>
                <span className="block text-sm font-bold text-text-primary">
                  Controls and chart labels
                </span>
                <span className="mt-0.5 block text-xs text-text-secondary">
                  Know every view, filter and bar.
                </span>
              </span>
            </span>
            <ChevronDownIcon
              aria-hidden="true"
              className={classNames('h-4 w-4 shrink-0 text-accent-blue', open && 'rotate-180')}
            />
          </Disclosure.Button>
          <Disclosure.Panel className="grid items-start gap-2 px-3 pb-3 pt-1 sm:grid-cols-2">
            {gexGuideControls.map(({ id, choices }) => {
              const lesson: GEXLesson = gexLessons[id];
              return (
                <Disclosure
                  key={id}
                  as="article"
                  data-gex-guide-lesson={id}
                  className="min-w-0 overflow-hidden rounded-lg bg-palette-black-1 ring-1 ring-inset ring-white/[0.07]"
                >
                  {({ open: expanded }) => (
                    <>
                      <Disclosure.Button className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-3 text-left text-[13px] font-semibold text-text-primary hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary">
                        {lesson.title}
                        <ChevronDownIcon
                          aria-hidden="true"
                          className={classNames(
                            'h-4 w-4 shrink-0 text-text-faded',
                            expanded && 'rotate-180',
                          )}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="px-3 pb-3">
                        <p className="text-pretty text-[13px] font-semibold leading-5 text-accent-blue">
                          {lesson.claim}
                        </p>
                        {choices ? (
                          <FactRows rows={choices} />
                        ) : (
                          <>
                            {lesson.rows && <FactRows rows={lesson.rows} />}
                            {lesson.cells && <FactCells cells={lesson.cells} />}
                            {lesson.facts && <FactList facts={lesson.facts} small />}
                          </>
                        )}
                        <ActionLines action={lesson.action} />
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              );
            })}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

export interface GEXHelpValue {
  label: string;
  value: React.ReactNode;
  tone?: GEXTone;
  /** Takes a full row of the card's grid, for a value too long for half (a change and its %). */
  wide?: boolean;
}

interface HelpProps {
  id: GEXLessonId;
  children?: React.ReactNode;
  className?: string;
  triggerClassName?: string;
  label?: string;
  lesson?: GEXLesson;
  /** One value prints beside the title; several print as a grid. */
  values?: GEXHelpValue[];
  /** The reading of the live state, shown under the claim. */
  interpretation?: string;
  /** An approved existing native hint, presented verbatim in the designed card. */
  nativeText?: string;
  /** A live graphic drawn from the number the card explains. */
  visual?: React.ReactNode;
  /** The choices of a control, with the active one lit. */
  options?: OptionCell[];
  /** The lesson cell that matches the live state, lit. */
  activeCell?: number;
  /** Icons for the lesson's rows, keyed by row label: the panel's own marks beside its legend. */
  rowIcons?: Record<string, React.ReactNode>;
  /** An inspector names one point: its values and reading, without the lesson's meaning. */
  variant?: 'lesson' | 'inspector';
  /**
   * An info icon beside the trigger. `always` keeps it in view on mouse screens; touch screens
   * hide it, and the title it follows is the tap target. `hover` hides it at rest on mouse and
   * trackpad screens and brings it in on hover or focus; touch screens show it.
   */
  hint?: 'always' | 'hover';
  iconOnly?: boolean;
  /** Inside a corner badge: no touch area beyond its box, so it takes no tap from the control. */
  badge?: boolean;
  /** In a table header, beside its label: a 16 by 24px box, never where a touch screen exists. */
  edge?: 'left' | 'right';
  tabIndex?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  /** Explain a control on hover/focus while its own click/tap keeps the primary action. */
  actionHost?: boolean;
}

interface HelpSurfaceProps extends HelpProps {
  open: boolean;
  close: (focusTarget?: HTMLElement) => void;
  /** How the resting trigger was first used; the live one picks up from there. */
  woke: Wake;
}

type Wake = 'hover' | 'focus' | 'click';

/** Lesson cards read at 360px; an inspector names one point at 300px. */
const cardWidth = (variant: HelpProps['variant']) => (variant === 'inspector' ? 300 : 360);

/**
 * A control group's help as a badge on its top-right corner, out of the layout. Put `group/gex
 * relative` on the group. On a mouse screen the badge stays out of sight and takes no click until
 * its control is hovered or focused, or while its card is open. A phone has no spare corner: a
 * badge there would take taps meant for the control, so touch screens leave it out, and the
 * control shows what it does when tapped.
 */
export const GEX_HELP_BADGE =
  'absolute -right-2.5 -top-2.5 z-10 rounded-full bg-palette-black-3 transition-opacity duration-150 [@media(pointer:coarse)]:hidden [@media(hover:hover)_and_(pointer:fine)]:pointer-events-none [@media(hover:hover)_and_(pointer:fine)]:opacity-0 group-hover/gex:pointer-events-auto group-hover/gex:opacity-100 group-focus-within/gex:pointer-events-auto group-focus-within/gex:opacity-100 group-has-[[aria-expanded=true]]/gex:pointer-events-auto group-has-[[aria-expanded=true]]/gex:opacity-100';

/** Hidden at rest where a pointer can hover; revealed with the trigger or its group. */
const HOVER_REVEAL =
  'transition-opacity duration-150 [@media(hover:hover)_and_(pointer:fine)]:opacity-0 group-hover/gex:opacity-100 group-focus-visible/gex:opacity-100 group-aria-expanded/gex:opacity-100';

const toneClass: Record<NonNullable<GEXHelpValue['tone']>, string> = {
  positive: 'text-bullish-green',
  negative: 'text-graph-red',
  accent: 'text-accent-blue',
  neutral: 'text-text-primary',
  warning: 'text-graph-yellow',
};

const toneText: Record<GEXTone, string> = {
  positive: 'text-bullish-green',
  negative: 'text-graph-red',
  accent: 'text-accent-blue',
  neutral: 'text-text-primary',
  warning: 'text-graph-yellow',
};

/** One fact line, or two or three bullets, each one line. */
function FactList({ facts, small = false }: { facts: string[]; small?: boolean }) {
  const size = small ? 'text-xs leading-5' : 'text-[13px] leading-5';
  if (facts.length === 1)
    return (
      <p data-gex-fact className={classNames(size, 'text-text-secondary')}>
        {facts[0]}
      </p>
    );
  return (
    <ul className="space-y-0.5">
      {facts.map((fact) => (
        <li
          key={fact}
          data-gex-fact
          className={classNames(size, 'flex items-baseline gap-2 text-text-secondary')}
        >
          <span
            aria-hidden="true"
            className="h-1 w-1 shrink-0 -translate-y-[3px] rounded-full bg-accent-blue/70"
          />
          {fact}
        </li>
      ))}
    </ul>
  );
}

/** A label and one line per row: a legend or a short definition list. */
function FactRows({ rows, icons }: { rows: GEXFact[]; icons?: Record<string, React.ReactNode> }) {
  return (
    <dl className="mt-2.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
      {rows.map((row) => (
        <React.Fragment key={row.label}>
          <dt
            className={classNames(
              'flex items-center gap-1.5 whitespace-nowrap text-[11px] font-bold leading-5',
              toneText[row.tone ?? 'neutral'],
            )}
          >
            {icons?.[row.label]}
            {row.label}
          </dt>
          <dd data-gex-fact className="text-xs leading-5 text-text-secondary">
            {row.line}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

/** Either-or facts side by side, with the live one lit. */
function FactCells({ cells, active }: { cells: GEXFact[]; active?: number }) {
  return (
    <div
      className={classNames(
        'mt-2.5 grid gap-1.5',
        cells.length === 3 ? 'grid-cols-3' : 'grid-cols-2',
      )}
    >
      {cells.map((cell, index) => {
        const on = index === active;
        return (
          <div
            key={cell.label}
            data-gex-option
            className={classNames(
              'rounded-lg px-2.5 py-1 ring-1 ring-inset',
              on
                ? cell.tone === 'negative'
                  ? 'bg-graph-red/[0.14] ring-graph-red/50'
                  : cell.tone === 'positive'
                  ? 'bg-bullish-green/[0.14] ring-bullish-green/50'
                  : 'bg-accent-blue/[0.12] ring-accent-blue/45'
                : 'bg-white/[0.03] ring-white/[0.06]',
            )}
          >
            <span
              className={classNames(
                'flex items-center justify-between gap-1 text-[11px] font-bold',
                cell.tone ? toneText[cell.tone] : 'text-text-primary',
              )}
            >
              {cell.label}
              {on && (
                <span className="text-[9px] font-bold uppercase tracking-wider text-text-primary">
                  Now
                </span>
              )}
            </span>
            <span className="block text-[11px] leading-4 text-text-secondary">{cell.line}</span>
          </div>
        );
      })}
    </div>
  );
}

const ACTION_ICON: Record<GEXActionIcon, typeof ArrowRightIcon> = {
  fade: ReplyIcon,
  up: TrendingUpIcon,
  down: TrendingDownIcon,
  across: ArrowRightIcon,
  column: ArrowDownIcon,
};

/** The one thing to do: a line behind the arrow, or two glyph lines when the reaction decides. */
function ActionLines({ action }: { action: GEXLesson['action'] }) {
  if (typeof action === 'string')
    return (
      <p
        data-gex-action
        className="mt-2.5 flex gap-2 text-[13px] font-medium leading-5 text-text-primary"
      >
        <ArrowRightIcon
          className="mt-[3px] h-3.5 w-3.5 shrink-0 text-accent-blue"
          aria-hidden="true"
        />
        <span>{action}</span>
      </p>
    );
  return (
    <ul data-gex-action className="mt-2.5 space-y-1">
      {action.map((step) => {
        const Icon = ACTION_ICON[step.icon];
        return (
          <li
            key={step.text}
            className="flex gap-2 text-[13px] font-medium leading-5 text-text-primary"
          >
            <Icon className="mt-[3px] h-3.5 w-3.5 shrink-0 text-accent-blue" aria-hidden="true" />
            <span>{step.text}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function placeGEXHelp(
  anchor: DOMRect | { left: number; right: number; top: number; bottom: number },
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
) {
  const panelWidth = Math.min(width, Math.max(0, viewportWidth - 24));
  const left = Math.max(12, Math.min(anchor.left, viewportWidth - panelWidth - 12));
  const below = Math.max(0, viewportHeight - anchor.bottom - 20);
  const above = Math.max(0, anchor.top - 20);
  const useBelow = height <= below || below >= above;
  const available = Math.max(below, above);
  const useViewport = available < 180;
  const maxHeight = useViewport
    ? Math.max(0, viewportHeight - 24)
    : Math.max(0, useBelow ? below : above);
  const top = useViewport
    ? 12
    : useBelow
    ? Math.min(anchor.bottom + 8, viewportHeight - 12)
    : Math.max(12, anchor.top - 8 - Math.min(height, maxHeight));
  return { left, top, width: panelWidth, maxHeight };
}

/** Section information stays visible on mouse and touch screens. */
function helpIcon(hint: HelpProps['hint'], size: string) {
  return (
    <InformationCircleIcon
      aria-hidden="true"
      className={classNames(
        size,
        'shrink-0',
        hint === 'hover'
          ? HOVER_REVEAL
          : 'text-text-faded transition-colors group-hover/gex:text-text-secondary',
      )}
    />
  );
}

/** One trigger look for the resting button and the live one, so waking a trigger moves nothing. */
const triggerProps = ({
  id,
  children,
  triggerClassName,
  label,
  lesson,
  iconOnly = false,
  badge = false,
  edge,
  tabIndex,
  onKeyDown,
}: HelpProps) => {
  const item: GEXLesson = lesson ?? gexLessons[id];
  return {
    type: 'button' as const,
    tabIndex,
    onKeyDown,
    'aria-haspopup': 'dialog' as const,
    // A trigger that wraps the page's own text keeps that text as its name, so a screen reader
    // reads the page as develop does; an icon alone gets a name of its own.
    'aria-label': iconOnly ? label ?? `Explain ${item.title}` : undefined,
    'data-gex-help': item.title,
    // Preflight resets a button's text-transform; the trigger keeps its label's casing.
    className: classNames(
      'group/gex inline-flex min-w-0 items-center gap-1 rounded text-left [text-transform:inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
      iconOnly &&
        "relative justify-center [@media(pointer:coarse)]:after:absolute [@media(pointer:coarse)]:after:content-['']",
      // A badge or a header icon sits beside a control of the page's own: any touch area beyond
      // its box would take that control's taps, so its box is the whole target. A header icon's
      // box is its glyph's width: a narrow header cell has 18px beside a right-aligned label.
      iconOnly &&
        !badge &&
        !edge &&
        '[@media(pointer:coarse)]:mx-2.5 [@media(pointer:coarse)]:after:-inset-2.5',
      iconOnly && !badge && children === undefined && (edge ? 'py-1' : 'p-1'),
      triggerClassName,
    ),
  };
};

function TriggerContent({ id, children, lesson, hint, iconOnly = false }: HelpProps) {
  if (children !== undefined)
    return (
      <>
        {children}
        {hint && helpIcon(hint, 'h-3.5 w-3.5')}
      </>
    );
  if (iconOnly) return helpIcon(hint, 'h-4 w-4');
  return (
    <>
      <span>{(lesson ?? gexLessons[id]).title}</span>
      {hint && helpIcon(hint, 'h-3.5 w-3.5')}
    </>
  );
}

interface HelpCardBodyProps {
  item: GEXLesson;
  inspector: boolean;
  headline: GEXHelpValue | null;
  grid: GEXHelpValue[] | null;
  interpretation?: string;
  nativeText?: string;
  rowIcons?: Record<string, React.ReactNode>;
  activeCell?: number;
  options?: OptionCell[];
  visual?: React.ReactNode;
  onClose: () => void;
  onGuide: () => void;
}

/** What every card prints, for the popover cards and the shared inspector alike. */
function HelpCardBody({
  item,
  inspector,
  headline,
  grid,
  interpretation,
  nativeText,
  rowIcons,
  activeCell,
  options,
  visual,
  onClose,
  onGuide,
}: HelpCardBodyProps) {
  return (
    <>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="min-w-0 text-[13px] font-bold leading-5 tracking-tight text-text-primary">
              {item.title}
            </h3>
            {headline && (
              <span className="flex shrink-0 items-baseline gap-1.5">
                {inspector && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-text-faded">
                    {headline.label}
                  </span>
                )}
                <span
                  className={classNames(
                    'text-[15px] font-bold tabular-nums leading-5',
                    toneClass[headline.tone ?? 'neutral'],
                  )}
                >
                  {headline.value}
                </span>
              </span>
            )}
          </div>
          {inspector && interpretation ? (
            <p
              className={classNames(
                'mt-0.5 text-pretty text-[13px] font-semibold leading-5',
                headline?.tone === 'negative'
                  ? 'text-graph-red'
                  : headline?.tone === 'positive'
                  ? 'text-bullish-green'
                  : 'text-accent-blue',
              )}
            >
              {interpretation}
            </p>
          ) : (
            <p className="mt-0.5 text-pretty text-[13px] font-semibold leading-5 text-accent-blue">
              {item.claim}
            </p>
          )}
        </div>
        <button
          type="button"
          aria-label={`Close ${item.title} explanation`}
          onClick={onClose}
          className="-mr-1.5 -mt-1 shrink-0 rounded p-1 text-text-faded hover:bg-white/[0.05] hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      {interpretation && !inspector && (
        <p className="mt-2.5 text-pretty border-l-2 border-accent-blue/60 pl-2.5 text-[13px] font-medium leading-5 text-text-primary">
          {interpretation}
        </p>
      )}
      {grid && (
        <dl className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg bg-white/[0.03] px-3 py-2 ring-1 ring-inset ring-white/[0.05]">
          {grid.map((row) => (
            <div
              key={row.label}
              className={classNames(
                'flex min-w-0 items-baseline justify-between gap-2',
                row.wide && 'col-span-2',
              )}
            >
              <dt className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-text-faded">
                {row.label}
              </dt>
              <dd
                className={classNames(
                  'truncate text-xs font-semibold tabular-nums',
                  toneClass[row.tone ?? 'neutral'],
                )}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {!inspector && item.facts && (
        <div className="mt-2">
          <FactList facts={item.facts} />
        </div>
      )}
      {!inspector && item.rows && <FactRows rows={item.rows} icons={rowIcons} />}
      {!inspector && item.cells && <FactCells cells={item.cells} active={activeCell} />}
      {!inspector && item.chips && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1" data-gex-chips>
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-text-faded">
            {item.chips.label}
          </span>
          {item.chips.items.map((chip) => (
            <span
              key={chip}
              className="rounded-md bg-white/[0.05] px-1.5 py-0.5 text-[11px] font-semibold text-text-primary ring-1 ring-inset ring-white/[0.07]"
            >
              {chip}
            </span>
          ))}
        </div>
      )}
      {options && (
        <div className="mt-2.5">
          <OptionCells options={options} />
        </div>
      )}
      {visual && <div className="mt-2.5">{visual}</div>}
      {nativeText && (
        <p className="mt-2.5 rounded-md bg-white/[0.03] px-2.5 py-2 text-[13px] leading-5 text-text-secondary">
          {nativeText}
        </p>
      )}
      <ActionLines action={item.action} />
      <div className="mt-1 flex justify-end">
        <button
          type="button"
          onClick={onGuide}
          className="inline-flex items-center gap-1 rounded px-1 text-[11px] font-semibold leading-4 text-accent-blue hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
        >
          Open GEX guide
          <ArrowRightIcon className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>
    </>
  );
}

function HelpSurface({
  id,
  children,
  className,
  triggerClassName,
  label,
  lesson: custom,
  values,
  interpretation,
  nativeText,
  visual,
  options,
  activeCell,
  rowIcons,
  variant = 'lesson',
  hint,
  iconOnly = false,
  badge = false,
  edge,
  tabIndex,
  onKeyDown,
  onFocus,
  open,
  close,
  woke,
  actionHost = false,
}: HelpSurfaceProps) {
  const education = useGEXEducation();
  const instance = useId();
  const openRef = useRef(open);
  openRef.current = open;
  const item: GEXLesson = custom ?? gexLessons[id];
  const triggerRef = useRef<HTMLElement>(null);
  const focusedControl = useRef<HTMLElement | null>(null);
  const returnFocusTarget = useCallback(() => {
    const trigger = triggerRef.current;
    if (!actionHost) return trigger;
    if (focusedControl.current?.isConnected && trigger?.contains(focusedControl.current))
      return focusedControl.current;
    return (
      trigger?.querySelector<HTMLElement>(
        'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]',
      ) ?? education.guideTrigger.current
    );
  }, [actionHost, education.guideTrigger]);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelNode, setPanelNode] = useState<HTMLDivElement | null>(null);
  const attachPanel = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    setPanelNode(node);
  }, []);
  const [preview, setPreview] = useState(false);
  const [placement, setPlacement] = useState<ReturnType<typeof placeGEXHelp> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visible = open || preview;
  const width = cardWidth(variant);
  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const dismissedAt = useRef(0);
  const pressedAt = useRef(0);
  const dismiss = useCallback(() => {
    clear();
    dismissedAt.current = Date.now();
    setPreview(false);
    education.release(instance);
    if (openRef.current)
      close(document.activeElement instanceof HTMLElement ? document.activeElement : undefined);
  }, [clear, close, education, instance]);
  const show = (intent: Wake = 'hover') => {
    clear();
    if (
      education.activate(
        instance,
        dismiss,
        openRef.current,
        () =>
          !!triggerRef.current?.contains(document.activeElement) ||
          !!panelRef.current?.contains(document.activeElement),
        intent,
      )
    )
      setPreview(true);
  };
  const leave = () => {
    clear();
    if (!openRef.current)
      timer.current = setTimeout(() => {
        const focused = document.activeElement;
        if (triggerRef.current?.contains(focused) || panelRef.current?.contains(focused)) return;
        setPreview(false);
        education.release(instance);
      }, 140);
  };
  useEffect(
    () => () => {
      clear();
      education.release(instance);
    },
    [clear, education, instance],
  );
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) education.release(instance);
    wasOpen.current = open;
  }, [open, education, instance]);
  // The resting button was replaced under the pointer or the focus; carry on where it left off.
  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    if (!actionHost && (woke === 'focus' || woke === 'click'))
      trigger.focus({ preventScroll: true });
    if (woke === 'click') trigger.click();
    if (woke === 'hover' && trigger.matches(':hover'))
      timer.current = setTimeout(() => show(), 120);
    // Mount only: later interactions use the live handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!visible || !panelNode) return;
    const place = () => {
      if (triggerRef.current && panelRef.current)
        setPlacement(
          placeGEXHelp(
            triggerRef.current.getBoundingClientRect(),
            width,
            panelRef.current.scrollHeight,
            window.innerWidth,
            window.innerHeight,
          ),
        );
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const owned =
        openRef.current ||
        !!triggerRef.current?.contains(document.activeElement) ||
        !!panelRef.current?.contains(document.activeElement);
      // A mouse hover preview closes and leaves the key and the focus to the page.
      if (owned) event.stopPropagation();
      dismiss();
      // Focus first: the trigger's focus opens a preview, which the next line closes.
      if (owned) returnFocusTarget()?.focus();
      setPreview(false);
    };
    const outside = (event: PointerEvent) => {
      if (
        !triggerRef.current?.contains(event.target as Node) &&
        !panelRef.current?.contains(event.target as Node)
      )
        dismiss();
    };
    const move = (event: MouseEvent) => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel || openRef.current) return;
      if (
        trigger.contains(event.target as Node) ||
        panel.contains(event.target as Node) ||
        isBetweenHelpSurfaces(
          event.clientX,
          event.clientY,
          trigger.getBoundingClientRect(),
          panel.getBoundingClientRect(),
        )
      )
        clear();
      else if (!timer.current) leave();
    };
    place();
    const observer = new ResizeObserver(place);
    if (panelRef.current) observer.observe(panelRef.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.addEventListener('keydown', escape, true);
    window.addEventListener('pointerdown', outside);
    window.addEventListener('mousemove', move);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('keydown', escape, true);
      window.removeEventListener('pointerdown', outside);
      window.removeEventListener('mousemove', move);
    };
  }, [visible, width, dismiss, panelNode, returnFocusTarget]);
  const placed = placement !== null;
  useEffect(() => {
    if (!open || !placed) return;
    // Position first: Headless UI skips focus while the initial portal is hidden.
    const frame = requestAnimationFrame(() =>
      panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true }),
    );
    return () => cancelAnimationFrame(frame);
  }, [open, placed]);

  const inspector = variant === 'inspector';
  const Panel = actionHost ? 'div' : Popover.Panel;
  // One value prints beside the title; an inspector also lists the rest in a compact grid.
  const headline = values && (inspector || values.length === 1) ? values[0] : null;
  const grid = values && values.length > 1 ? (inspector ? values.slice(1) : values) : null;

  const handlers = {
    onMouseEnter: () => {
      clear();
      timer.current = setTimeout(() => show(), 120);
    },
    onMouseLeave: leave,
    onPointerDown: () => {
      pressedAt.current = Date.now();
      if (actionHost) dismiss();
    },
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      if (actionHost && event.target instanceof HTMLElement) focusedControl.current = event.target;
      if (actionHost && triggerRef.current?.contains(event.relatedTarget as Node)) return;
      if (
        Date.now() - dismissedAt.current > 300 &&
        (!actionHost || Date.now() - pressedAt.current > 800)
      )
        show('focus');
      if (!actionHost) onFocus?.(event as React.FocusEvent<HTMLButtonElement>);
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      if (
        !panelRef.current?.contains(event.relatedTarget as Node) &&
        !triggerRef.current?.contains(event.relatedTarget as Node)
      )
        leave();
    },
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      if (actionHost) {
        dismiss();
        return;
      }
      event.stopPropagation();
      clear();
      education.activate(
        instance,
        dismiss,
        true,
        () =>
          !!triggerRef.current?.contains(document.activeElement) ||
          !!panelRef.current?.contains(document.activeElement),
        'click',
      );
      setPreview(false);
    },
  };
  return (
    <>
      {actionHost ? (
        <span
          ref={triggerRef as React.RefObject<HTMLSpanElement>}
          id={`${instance}-trigger`}
          data-gex-control-help={item.title}
          aria-describedby={visible ? `${instance}-content` : undefined}
          className={triggerClassName}
          tabIndex={tabIndex}
          role={tabIndex === 0 ? 'button' : undefined}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLSpanElement>}
          {...handlers}
        >
          {children}
        </span>
      ) : (
        <Popover.Button
          ref={triggerRef as React.RefObject<HTMLButtonElement>}
          id={`${instance}-trigger`}
          {...triggerProps({
            id,
            children,
            triggerClassName,
            label,
            lesson: custom,
            iconOnly,
            badge,
            edge,
            tabIndex,
            onKeyDown,
          })}
          aria-describedby={visible ? `${instance}-content` : undefined}
          {...handlers}
        >
          <TriggerContent id={id} lesson={custom} hint={hint} iconOnly={iconOnly}>
            {children}
          </TriggerContent>
        </Popover.Button>
      )}
      {visible && typeof document !== 'undefined' && (
        <Portal>
          <Panel
            {...(!actionHost ? { static: true, focus: open } : {})}
            ref={attachPanel}
            id={`${instance}-content`}
            data-gex-education-overlay
            data-gex-education-trigger={`${instance}-trigger`}
            role="dialog"
            aria-label={item.title}
            className={classNames(
              'fixed z-[80] overflow-y-auto rounded-xl bg-palette-black-1 p-3 text-left shadow-2xl shadow-black/60 ring-1 ring-white/[0.09]',
              className,
            )}
            style={placement ?? { visibility: 'hidden', width, left: -9999, top: -9999 }}
            onMouseEnter={clear}
            onMouseLeave={leave}
            onFocus={clear}
            onBlur={(event) => {
              if (
                !panelRef.current?.contains(event.relatedTarget as Node) &&
                !triggerRef.current?.contains(event.relatedTarget as Node)
              )
                leave();
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <HelpCardBody
              item={item}
              inspector={inspector}
              headline={headline}
              grid={grid}
              interpretation={interpretation}
              nativeText={nativeText}
              rowIcons={rowIcons}
              activeCell={activeCell}
              options={options}
              visual={visual}
              onClose={() => {
                dismiss();
                returnFocusTarget()?.focus();
                setPreview(false);
              }}
              onGuide={() => {
                dismiss();
                education.openGuide(item.chapter, returnFocusTarget());
              }}
            />
          </Panel>
        </Portal>
      )}
    </>
  );
}

interface InspectorProps {
  /** The element the card points at; nothing renders while it is null. */
  anchor: HTMLElement | null;
  /** Opened by a click, a tap or Enter: the card takes focus and stays until dismissed. */
  pinned: boolean;
  lesson: GEXLesson;
  values: GEXHelpValue[];
  interpretation?: string;
  onClose: () => void;
  /** The pointer moved onto the card or off it, so a hover preview can stay or go. */
  onHoverChange?: (over: boolean) => void;
}

/**
 * One inspector for a grid of readings, such as the heatmap's cells: the cells stay plain and the
 * card follows the cell in use. A popover per cell would cost every live update.
 */
export function GEXInspector({
  anchor,
  pinned,
  lesson,
  values,
  interpretation,
  onClose,
  onHoverChange,
}: InspectorProps) {
  const education = useGEXEducation();
  const instance = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelNode, setPanelNode] = useState<HTMLDivElement | null>(null);
  const attach = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    setPanelNode(node);
  }, []);
  const [placement, setPlacement] = useState<ReturnType<typeof placeGEXHelp> | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const hoverRef = useRef(onHoverChange);
  hoverRef.current = onHoverChange;
  const visible = anchor !== null;
  useEffect(() => () => education.release(instance), [education, instance]);

  // A deliberate inspector selection replaces another card; incidental hover leaves a pin open.
  useEffect(() => {
    if (
      visible &&
      !education.activate(
        instance,
        () => closeRef.current(),
        pinned,
        () =>
          !!anchor?.contains(document.activeElement) ||
          !!panelRef.current?.contains(document.activeElement),
        pinned ? 'click' : anchor?.contains(document.activeElement) ? 'focus' : 'hover',
      )
    )
      closeRef.current();
    if (!visible) education.release(instance);
  }, [visible, anchor, pinned, education, instance]);

  useEffect(() => {
    if (!anchor || !panelNode) return;
    const place = () =>
      setPlacement(
        placeGEXHelp(
          anchor.getBoundingClientRect(),
          cardWidth('inspector'),
          panelNode.scrollHeight,
          window.innerWidth,
          window.innerHeight,
        ),
      );
    place();
    const observer = new ResizeObserver(place);
    observer.observe(panelNode);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, panelNode]);

  useEffect(() => {
    if (!anchor) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const owned =
        pinned ||
        anchor === document.activeElement ||
        !!panelRef.current?.contains(document.activeElement);
      if (owned) event.stopPropagation();
      closeRef.current();
      if (owned) anchor.focus({ preventScroll: true });
    };
    const outside = (event: PointerEvent) => {
      if (
        !anchor.contains(event.target as Node) &&
        !panelRef.current?.contains(event.target as Node)
      )
        closeRef.current();
    };
    const move = (event: MouseEvent) => {
      const panel = panelRef.current;
      if (pinned || !panel) return;
      // The grid owns its pending cell switch. Do not turn that opening timer into a departure.
      const cell =
        event.target instanceof Element ? event.target.closest('td[data-cell-key]') : null;
      const inCorridor = isBetweenHelpSurfaces(
        event.clientX,
        event.clientY,
        anchor.getBoundingClientRect(),
        panel.getBoundingClientRect(),
      );
      if (
        cell &&
        cell.closest('table') === anchor.closest('table') &&
        !inCorridor &&
        !panel.contains(event.target as Node)
      )
        return;
      hoverRef.current?.(
        anchor.contains(event.target as Node) || panel.contains(event.target as Node) || inCorridor,
      );
    };
    window.addEventListener('keydown', escape, true);
    window.addEventListener('pointerdown', outside);
    window.addEventListener('mousemove', move);
    return () => {
      window.removeEventListener('keydown', escape, true);
      window.removeEventListener('pointerdown', outside);
      window.removeEventListener('mousemove', move);
    };
  }, [anchor, pinned]);

  const placed = placement !== null;
  useEffect(() => {
    if (!pinned || !placed) return;
    const frame = requestAnimationFrame(() =>
      panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true }),
    );
    return () => cancelAnimationFrame(frame);
  }, [pinned, placed]);

  if (!anchor || typeof document === 'undefined') return null;
  return (
    <Portal>
      <div
        ref={attach}
        role="dialog"
        aria-label={lesson.title}
        data-gex-education-overlay
        className="fixed z-[80] overflow-y-auto rounded-xl bg-palette-black-1 p-3 text-left shadow-2xl shadow-black/60 ring-1 ring-white/[0.09]"
        style={
          placement ?? {
            visibility: 'hidden',
            width: cardWidth('inspector'),
            left: -9999,
            top: -9999,
          }
        }
        onMouseEnter={() => onHoverChange?.(true)}
        onMouseLeave={() => onHoverChange?.(false)}
        onFocus={() => onHoverChange?.(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) onHoverChange?.(false);
        }}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          // The card lives in a portal at the end of the page. Tab moves between its controls as
          // usual; Tab past the last one, or Shift+Tab before the first, goes back to the cell.
          if (event.key !== 'Tab') return;
          const controls = [
            ...event.currentTarget.querySelectorAll<HTMLElement>(
              'button, a[href], [tabindex]:not([tabindex="-1"])',
            ),
          ];
          const edge = event.shiftKey ? controls[0] : controls[controls.length - 1];
          if (edge && event.target !== edge) return;
          event.preventDefault();
          closeRef.current();
          anchor.focus({ preventScroll: true });
        }}
      >
        <HelpCardBody
          item={lesson}
          inspector
          headline={values[0] ?? null}
          grid={values.length > 1 ? values.slice(1) : null}
          interpretation={interpretation}
          onClose={() => {
            closeRef.current();
            anchor.focus({ preventScroll: true });
          }}
          onGuide={() => {
            closeRef.current();
            education.openGuide(lesson.chapter, anchor);
          }}
        />
      </div>
    </Portal>
  );
}

/** Passive labels open help directly. Sortable labels preview help and keep their sort action. */
export function GEXHeaderHelp({
  id,
  children,
  sortable = false,
}: {
  id: GEXLessonId;
  side: 'left' | 'right';
  children: React.ReactNode;
  sortable?: boolean;
}) {
  return (
    <GEXHelp
      id={id}
      actionHost={sortable}
      triggerClassName="inline-flex items-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      tabIndex={sortable ? 0 : undefined}
      onKeyDown={
        sortable
          ? (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.currentTarget.click();
            }
          : undefined
      }
    >
      {children}
    </GEXHelp>
  );
}

/**
 * Repeated rows (a list of trade cards, a row of date headers) keep one set of help in the tab
 * order: triggers inside a quiet scope take no tab stop unless they set their own.
 */
const QuietContext = createContext(false);
export function GEXHelpQuiet({
  quiet = true,
  children,
}: {
  quiet?: boolean;
  children: React.ReactNode;
}) {
  return <QuietContext.Provider value={quiet}>{children}</QuietContext.Provider>;
}

/**
 * A resting button until its first hover, focus or click: the page holds hundreds of triggers,
 * and a popover for each would cost every live update. The live trigger looks and sits the same.
 */
function RestingTrigger({ onWake, ...props }: HelpProps & { onWake: (wake: Wake) => void }) {
  // A press (mouse, pen or touch) focuses the button on the way down; waking then would swap the
  // button out before its click. A press wakes it on the click instead; only a keyboard focus
  // wakes it on focus, and only a real mouse wakes it on hover.
  const pressedAt = useRef(0);
  return (
    <span className="inline-flex min-w-0">
      <button
        {...triggerProps(props)}
        aria-expanded={false}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') onWake('hover');
        }}
        onPointerDown={() => {
          pressedAt.current = Date.now();
        }}
        onFocus={(event) => {
          props.onFocus?.(event);
          if (Date.now() - pressedAt.current > 800) onWake('focus');
        }}
        onClick={(event) => {
          event.stopPropagation();
          onWake('click');
        }}
      >
        <TriggerContent {...props} />
      </button>
    </span>
  );
}

export function GEXHelp(props: HelpProps) {
  const { enabled } = useGEXEducation();
  const quiet = useContext(QuietContext);
  const [woke, setWoke] = useState<Wake | null>(null);
  if (!enabled)
    return props.iconOnly ? null : (
      <>{props.children ?? props.label ?? gexLessons[props.id].title}</>
    );
  const tabIndex = props.tabIndex ?? (quiet ? -1 : undefined);
  if (props.actionHost)
    return (
      <HelpSurface {...props} tabIndex={tabIndex} open={false} close={() => {}} woke="hover" />
    );
  if (!woke) return <RestingTrigger {...props} tabIndex={tabIndex} onWake={setWoke} />;
  return (
    <Popover as="span" className="inline-flex min-w-0">
      {({ open, close }) => (
        <HelpSurface {...props} tabIndex={tabIndex} open={open} close={close} woke={woke} />
      )}
    </Popover>
  );
}

/**
 * The guide's door: a book icon after the page title. The hover label stays out of the page's
 * text at rest (display: none) and names the control on hover and keyboard focus.
 */
export function GEXGuideButton() {
  const { enabled, openGuide, guideTrigger } = useGEXEducation();
  if (!enabled) return null;
  return (
    <button
      ref={guideTrigger}
      type="button"
      onClick={(event) => openGuide('regime', event.currentTarget)}
      className="group/guide relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-text-faded transition-colors hover:bg-white/[0.05] hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary [@media(pointer:coarse)]:after:absolute [@media(pointer:coarse)]:after:-inset-2.5 [@media(pointer:coarse)]:after:content-['']"
      aria-label="How to use Gamma Exposure"
    >
      <BookOpenIcon className="h-4 w-4" aria-hidden="true" />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-full z-[70] mt-1.5 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-palette-black-1 px-2 py-1 text-[11px] font-semibold text-text-primary shadow-lg ring-1 ring-white/[0.09] group-hover/guide:block group-focus-visible/guide:block"
      >
        GEX guide
      </span>
    </button>
  );
}
